import {
    APIParams,
    BaseFeatureArgs,
    FilterObject,
    QueryKeyMap,
    QueryState,
    RangeValue,
} from '@common/types/queryState';
import { isEmpty } from '../typeGuards';

const mapKey = (key: string, keyMap: QueryKeyMap): string => {
    return keyMap[key] || key;
};

const formatFilterValue = (item: FilterObject, fieldName: string): string => {
    if (typeof item === 'string') {
        return `"${item}"`;
    }

    const { value, operator } = item;

    switch (operator) {
        case 'missing':
            return `-(${fieldName}:*)`; // `(*:* -${fieldName}:*)`;
        case 'contains':
            return `*${value}*`;
        case 'startsWith':
            return `${value}*`;
        case 'endsWith':
            return `*${value}`;
        case 'exact':
        default:
            return `"${value}"`;
    }
};

const formatRangeValue = (range: RangeValue): string | null => {
    const { min = '*', max = '*', operator = 'inclusive' } = range;

    if (min === '*' && max === '*') return null;

    switch (operator) {
        case 'exclusive':
            return `{${min} TO ${max}}`;
        case 'greaterThan':
            return `{${min} TO *]`;
        case 'lesserThan':
            return `[* TO ${max}}`;
        case 'inclusive':
        default:
            return `[${min} TO ${max}]`;
    }
};

export const mapQueryStateToApiParams = (
    queryState: QueryState,
    featureArgs: BaseFeatureArgs,
): APIParams => {
    const params: APIParams = { ...featureArgs.queryParams };
    const fq: string[] = [];

    const keyMap = featureArgs.keyMap || {};
    const searchString = queryState.search.trim();

    // Query
    if (
        searchString &&
        featureArgs.searchTargetFields &&
        !isEmpty(featureArgs.searchTargetFields)
    ) {
        const fields = featureArgs.searchTargetFields?.map((field) =>
            mapKey(field, keyMap),
        );

        const expressions = fields?.map(
            (field) => `${field}:*${searchString.replaceAll(' ', '\\ ')}*`,
        );

        fq.push(`(${expressions.join(' OR ')})`);
    } else if (searchString) {
        params.q = `*${searchString.replaceAll(' ', '\\ ')}*`;
    }

    // Pagination
    params.offset = (queryState.pageIndex * queryState.pageSize).toString();
    params.limit = queryState.pageSize.toString();

    // Sorting
    params.sort = queryState.sorting
        .map((sortItem) => {
            const field = mapKey(sortItem.id, keyMap);
            const order = sortItem.desc ? 'desc' : 'asc';
            return `${field}+${order}`;
        })
        .join(',');

    // Facets filters & ranges
    Object.entries(queryState.facets).forEach(([key, values]) => {
        if (!values || values.length === 0) return;
        const field = mapKey(key, keyMap);

        const formattedValues = values.map((item) =>
            formatFilterValue(item, field),
        );
        const joinedValues = formattedValues.join(' OR ');

        fq.push(`${field}:(${joinedValues})`);
    });

    Object.entries(queryState.ranges).forEach(([key, range]) => {
        const field = mapKey(key, keyMap);
        const rangeString = formatRangeValue(range);

        if (rangeString) {
            fq.push(`${field}:${rangeString}`);
        }
    });

    // Fixed filters
    if (featureArgs.overrideFilterGenerator) {
        fq.push(...featureArgs.overrideFilterGenerator(featureArgs));
    } else if (
        featureArgs.fixedFilterGenerators &&
        !isEmpty(featureArgs.fixedFilterGenerators)
    ) {
        const fixedFilters: string[] =
            featureArgs.fixedFilterGenerators.flatMap((generator) =>
                generator(featureArgs),
            );

        if (!isEmpty(fixedFilters)) fq.push(...fixedFilters);
    }

    if (!isEmpty(fq)) params.fq = fq;

    // Facet counting / consulting
    if (featureArgs.facetFields && !isEmpty(featureArgs.facetFields)) {
        const fields = featureArgs.facetFields.map((field) =>
            mapKey(field, keyMap),
        );
        params.facet = true;
        params['facet.field'] = fields;
    }

    // Grouping
    if (queryState.groupField) {
        params.group = true;
        params['group.field'] = mapKey(queryState.groupField, keyMap);
    }

    // Field List (fl) for light responses
    if (featureArgs.fields && !isEmpty(featureArgs.fields)) {
        params.fl = featureArgs.fields
            .map((field) => mapKey(field, keyMap))
            .join(',');
    }

    // Custom fields
    if (queryState.custom) {
        Object.entries(queryState.custom).forEach(([key, value]) => {
            params[key] = value;
        });
    }

    return params;
};
